// src/components/User/Home/HomePage/ExperienceSection.js
import React from "react";

const EXPERIENCES = [
    {
        role: "Independent Consultant",
        company: null,
        period: "2025–Present",
        description:
            "I work with founders on growth problems across acquisition, engagement, retention, monetization and customer economics.",
        tags: [
            "Growth strategy",
            "Product analytics",
            "Experimentation",
            "Customer economics",
        ],
        isCurrent: true,
    },
    {
        role: "Founder",
        company: "Sellvoy",
        url: "https://sellvoy.com",
        period: "2026–Present",
        description: (
            <>
                Built an e-commerce intelligence platform combining data engineering,
                backend systems and AI. Designed automated systems that process
                approximately <strong className="font-semibold text-[var(--text-primary)]">30,000 websites per day</strong>.
            </>
        ),
        tags: ["Data engineering", "Backend", "AI/ML", "Product"],
        isCurrent: true,
    },
    {
        role: "Growth Manager",
        company: "Country Delight",
        period: "2022–2025",
        description:
            "Worked across the consumer business, taking ownership of problems spanning acquisition, engagement, retention, referrals, category management, AOV, CAC and offers. Led cross-functional growth initiatives across Engineering, Design, Operations and Customer Success.",
        tags: [
            "Acquisition",
            "Engagement",
            "Retention",
            "Referral",
            "AOV",
            "CAC",
            "Margin",
        ],
    },
    {
        role: "Founder",
        company: "Prep Junction",
        period: "2020–2022",
        description: (
            <>
                Built a placement-preparation platform for consulting and banking
                roles, onboarding <strong className="font-semibold text-[var(--text-primary)]">42 colleges and 12,000+ students</strong> across India.
                Owned product, partnerships and operations end-to-end.
            </>
        ),
        tags: ["0 → 1", "Go-to-market", "Partnerships", "Product"],
    },
];

const EARLIER_ROLES = [
    { company: "ICICI Bank", role: "Relationship Manager" },
    { company: "Nurturing Green", role: "Pre-sales & Business Development" },
    { company: "Simplex Infrastructures", role: "Assistant Engineer" },
];

export default function ExperienceSection({
    photo = "/assets/anindya.png",
}) {
    return (
        <section
            id="experience"
            className="px-6 md:px-12 lg:px-36 py-10 md:py-12 scroll-mt-14 border-t border-[rgba(0,0,0,0.06)]"
            style={{ backgroundColor: "var(--bg-base)" }}
        >
            <div className="max-w-[900px] mx-auto">

                {/* SECTION HEADER & BIO SUMMARY */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pb-6 border-b border-[rgba(0,0,0,0.08)]">
                    <div className="flex items-center gap-5">
                        <img
                            src={photo}
                            alt="Anindya Chowdhury"
                            loading="lazy"
                            width={72}
                            height={72}
                            className="w-16 h-16 sm:w-18 sm:h-18 rounded-full object-cover shadow-sm border border-[rgba(0,0,0,0.08)] flex-shrink-0"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                        <div>
                            <h2
                                className="text-3xl md:text-4xl font-extrabold tracking-tight"
                                style={{ color: "var(--text-primary)" }}
                            >
                                Experience
                            </h2>
                            <p className="text-sm md:text-base mt-1" style={{ color: "var(--text-secondary)" }}>
                                8+ years experience · Consumer Growth · Product · Technology · MBA, Indian Institute of Management Rohtak
                            </p>
                        </div>
                    </div>

                    {/* LinkedIn connect */}
                    <a
                        href="https://www.linkedin.com/in/anindya114/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-slate-200 hover:border-slate-400 hover:bg-[var(--bg-surface)] hover:no-underline transition shadow-sm"
                        style={{ color: "var(--text-primary)" }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="text-[#0A66C2]"
                            aria-hidden="true"
                        >
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764c.967 0 1.75.79 1.75 1.764s-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-1.337-.027-3.059-1.865-3.059-1.867 0-2.153 1.459-2.153 2.968v5.695h-3v-11h2.879v1.507h.041c.401-.758 1.379-1.559 2.84-1.559 3.04 0 3.605 2.003 3.605 4.609v6.443z" />
                        </svg>
                        <span>LinkedIn</span>
                    </a>
                </div>

                {/* TIMELINE LIST */}
                <div className="mt-8 space-y-8">
                    {EXPERIENCES.map((exp, idx) => (
                        <div
                            key={idx}
                            className="group relative pl-0 sm:pl-6 border-l-0 sm:border-l-2 border-slate-200 transition-colors hover:border-[var(--color-primary)]"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 mb-2">
                                <div className="flex items-center gap-2">
                                    <h3
                                        className="text-xl md:text-2xl font-bold tracking-tight"
                                        style={{ color: "var(--text-primary)" }}
                                    >
                                        {exp.role}
                                    </h3>
                                    {exp.isCurrent && (
                                        <span className="text-[11px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                            Current
                                        </span>
                                    )}
                                </div>
                                <div
                                    className="text-sm md:text-base font-semibold"
                                    style={{ color: "var(--color-primary)" }}
                                >
                                    {exp.company && (
                                        <>
                                            {exp.url ? (
                                                <a
                                                    href={exp.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 hover:underline text-[var(--color-primary)]"
                                                >
                                                    {exp.company}
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        className="opacity-75"
                                                    >
                                                        <line x1="7" y1="17" x2="17" y2="7"></line>
                                                        <polyline points="7 7 17 7 17 17"></polyline>
                                                    </svg>
                                                </a>
                                            ) : (
                                                exp.company
                                            )}
                                            {" · "}
                                        </>
                                    )}
                                    <span className="font-normal text-[var(--text-secondary)]">{exp.period}</span>
                                </div>
                            </div>

                            <p
                                className="text-base sm:text-lg leading-relaxed mt-2"
                                style={{ color: "#334155" }}
                            >
                                {exp.description}
                            </p>

                            {/* Tags */}
                            {exp.tags && exp.tags.length > 0 && (
                                <div className="mt-4 flex flex-wrap items-center gap-2">
                                    {exp.tags.map((tag, tIdx) => (
                                        <span
                                            key={tIdx}
                                            className="text-xs font-medium px-2.5 py-1 rounded-md bg-[rgba(0,0,0,0.04)] text-[var(--text-primary)] border border-[rgba(0,0,0,0.06)]"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* EARLIER EXPERIENCE */}
                <div className="mt-12 pt-8 border-t border-[rgba(0,0,0,0.08)]">
                    <h4
                        className="text-xs sm:text-sm font-semibold tracking-wider uppercase mb-4"
                        style={{ color: "var(--text-secondary)" }}
                    >
                        Earlier Experience
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {EARLIER_ROLES.map((role, rIdx) => (
                            <div
                                key={rIdx}
                                className="p-4 rounded-xl border border-[rgba(0,0,0,0.06)] bg-[var(--bg-surface)] shadow-xs"
                            >
                                <p
                                    className="font-bold text-sm md:text-base"
                                    style={{ color: "var(--text-primary)" }}
                                >
                                    {role.company}
                                </p>
                                <p
                                    className="text-xs md:text-sm mt-0.5"
                                    style={{ color: "var(--text-secondary)" }}
                                >
                                    {role.role}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </section>
    );
}
